// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {IconAdd} from '@deere/icons';
import EditLink from 'Ui/components/common/data-table/edit-link';
import OnlinkButton from 'Ui/components/common/onlink-button';
import {alphaNumericCompare, dateCompare} from 'Ui/models/maintenance';
import {deleteManualData, getManualDataHistory} from 'Services/manual-data-service';
import {configForUnit} from 'Utils/data-utils';
import {getDataUnit} from 'Utils/manual-data-utils';
import {fetchEffectData, useDeepEffect} from 'Utils/react-utils';
import {forceLocalTimeZone, formatLocalizedTime} from 'Utils/time-utils';
import {convertToUoM, formatNumber, formatUoMValue, getFormattedCurrency} from 'Utils/unit-conversion-utils';
import {isNullOrUndefined} from 'Common/utils/validation-utils';
import {closeDialog, openDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import {DOLLARS, DOLLARS_PER_KILOWATT_HOUR, DOLLARS_PER_LITER, DOLLARS_PER_THOUSAND_LITERS, FEET_AND_INCHES, GALLONS, LITERS} from 'Common/constants/data-unit-constants';
import {FUEL, FUEL_COST, GAS_COST, WATER_FLOW, WATER_COST, GAS, WATER_TRANSFER, WATER_FLOW_NIGHT, WATER_TRANSFER_NIGHT} from 'Common/constants/data-group-constants';
import {ADD_COURSE_MEASUREMENT_DATA, DELETE_COURSE_MEASUREMENT_DATA, EDIT_COURSE_MEASUREMENT_DATA} from 'Common/constants/business-activities';
import {isAuthorized} from 'Common/utils/authorization-handler';
import MediaQuery, {MOBILE_MEDIA_QUERY} from 'Ui/components/higher-order-components/media-query';
import DataGridWrapper from '../common/data-table/data-grid-wrapper';
import {Box, Stack} from '@deere/isg.component-library';

const MULTI_VALUE_DATA_TYPES = [WATER_FLOW, WATER_TRANSFER, WATER_FLOW_NIGHT, WATER_TRANSFER_NIGHT];

const MAX_ROW_LIST_ITEMS = 3;
const ESTIMATED_ROW_HEIGHT = 52;
const FIELD_WIDTHS = {
    [WATER_FLOW]: 175,
    [FUEL]: 140,
    DEFAULT: 160
};

function createColumnOrDefault(shouldCreate, column) {
    return shouldCreate ? [column] : [];
}

function getDataGridColumns(translations, form, openManualDataEntryDialog, onClose, label, setColumns) {
    const manualDataColumns = form.fields?.map((fields) => ({
        headerName: translations[fields.label],
        field: fields.dataType,
        sortable: !MULTI_VALUE_DATA_TYPES.includes(fields.dataType),
        sortComparator: alphaNumericCompare,
        width: FIELD_WIDTHS[form.dataGroup] || FIELD_WIDTHS.DEFAULT,
        flex: 1,
        renderCell: (params) => {
            const {value} = params;

            if (Array.isArray(value)) {
                const listItems = value.slice(0, MAX_ROW_LIST_ITEMS).map((item, i) => (
                    <Stack
                        direction='row'
                        key={`${item.location}-${item.formattedValue}-${i}`}
                        spacing={1.25}
                        sx={{
                            width: '100%'
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {item.location}
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {item.formattedValue}
                        </Box>
                    </Stack>
                ));

                return value.length > MAX_ROW_LIST_ITEMS ? [...listItems, <div key='ellipsis'>{'...'}</div>] : listItems;
            }

            return value;
        }
    }));

    setColumns([
        {
            headerName: translations.DATE,
            field: 'timeSample',
            sortComparator: dateCompare,
            width: 120,
            renderCell: (params) => (
                <EditLink
                    onClick={() => openManualDataEntryDialog({
                        dataGroup: form.dataGroup,
                        dataTypeTitle: label,
                        manualDataId: params.row.manualDataId,
                        onClose,
                        translations
                    })}
                    requiredMyJdPermissions={[EDIT_COURSE_MEASUREMENT_DATA]}
                >
                    {params.row.date}
                </EditLink>
            )
        },
        ...createColumnOrDefault(!form.isAssignment, {
            headerName: translations.TIME,
            field: 'time',
            valueGetter: (_value, row) => row.timeSample,
            sortComparator: dateCompare,
            renderCell: (params) => params.row.time,
            width: 100
        }),
        ...createColumnOrDefault(form.needsHole, {
            headerName: translations.ONLINK_HOLE,
            field: 'hole',
            sortComparator: alphaNumericCompare,
            width: 120
        }),
        ...createColumnOrDefault(form.needsHoleLocation, {
            headerName: translations.AREA,
            field: 'holeLocation',
            sortComparator: alphaNumericCompare,
            width: 120
        }),
        ...manualDataColumns || [],
        {
            headerName: translations.NOTES,
            field: 'note',
            minWidth: 100,
            flex: 1
        }
    ]);
}

function getFormattedDataUnitValue(value, {
    currencyPreference,
    dataType,
    dataUnit,
    translations
}) {
    const formattingDataUnit = getDataUnit({
        currencyPreference,
        dataType,
        dataUnit,
        translations
    });

    const formatting = configForUnit(formattingDataUnit);

    const processedValue = convertToUoM(value, {
        dataType,
        dataUnit,
        useNonConverted: true
    });

    return formatUoMValue(processedValue, {
        formatting: {
            ...formatting,
            dataUnits: Array.isArray(formatting.dataUnits) ? formatting.dataUnits : [formatting],
            noDecimalFormatting: formattingDataUnit !== FEET_AND_INCHES
        },
        translations
    });
}

function formatRowValue(value, {
    currencyPreference,
    dataType,
    dataUnit,
    translations
}) {
    switch (true) {
        case isNullOrUndefined(value): return '';
        case dataUnit === DOLLARS && ![FUEL_COST, GAS_COST].includes(dataType): return getFormattedCurrency(value, {
            currencyPreference
        });
        case [FUEL_COST, GAS_COST].includes(dataType): {
            const formattedCurrency = getFormattedCurrency(value, {
                currencyPreference
            });
            const perUnit = dataUnit === DOLLARS_PER_LITER ? translations.ONLINK_LITER : translations.ONLINK_GALLON;

            return `${formattedCurrency}/${perUnit}`;
        }
        case dataType === WATER_COST: {
            const formattedCurrency = getFormattedCurrency(value, {
                currencyPreference
            });
            const perUnit = dataUnit === DOLLARS_PER_THOUSAND_LITERS ? `1000 ${translations.STANDARD_UOM_LITER}` : `1000 ${translations.GALLONS}`;

            return `${formattedCurrency}/${perUnit}`;
        }
        case [FUEL, GAS].includes(dataType): {
            const unit = dataUnit === GALLONS ? translations.GALLONS : translations.STANDARD_UOM_LITER;

            return `${value} ${unit}`;
        }
        case dataUnit === DOLLARS_PER_KILOWATT_HOUR: {
            const formattedValue = getFormattedCurrency(value, {
                currencyPreference
            });

            return `${formattedValue}/${translations.ONLINK_KWH}`;
        }
        case MULTI_VALUE_DATA_TYPES.includes(dataType): {
            const unit = dataUnit === LITERS ? translations.STANDARD_UOM_LITER : translations.GALLONS;

            return `${formatNumber(value)} ${unit}`;
        }
        default: return getFormattedDataUnitValue(value, {
            currencyPreference,
            dataType,
            dataUnit,
            translations
        });
    }
}

function getFormattedValue(value, dataValues, formattingConfig) {
    if (!isNullOrUndefined(dataValues)) {
        return dataValues?.map((dataValue) => ({
            formattedValue: formatRowValue(dataValue.data_value, formattingConfig),
            location: dataValue.sub
        }));
    }

    return formatRowValue(value, formattingConfig);
}

function formatManualData(data, translations, currencyPreference) {
    return data?.map((rowEntry) => {
        const timeStampDate = forceLocalTimeZone(rowEntry.timeSample);

        const date = formatLocalizedTime(timeStampDate, {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });

        const time = formatLocalizedTime(timeStampDate, {
            hour: 'numeric',
            minute: 'numeric'
        });

        const manualData = rowEntry.jsonData?.reduce((manualDataObject, manualValue) => {
            const {
                data_type: dataType,
                data_unit: dataUnit,
                data_values: dataValues,
                value
            } = manualValue;

            const dataTypeValue = dataType.replace(/_volume|_weight|_f355_equivalents|_gravities/, '');

            return {
                ...manualDataObject,
                [dataTypeValue]: getFormattedValue(value, dataValues, {
                    currencyPreference,
                    dataType,
                    dataUnit,
                    translations
                })
            };
        }, {});

        return {
            ...rowEntry,
            ...manualData,
            date,
            time
        };
    });
}

function getAddButton(translations, form, openManualDataEntryDialog, onClose, label, addButtonLabel, isMobile) {
    const btnWrapperStyle = isMobile ? {
        position: 'absolute',
        bottom: '32px'
    } : null;
    const variant = isMobile ? 'borderless' : 'primary';

    return (
        <div
            className='extra-table-options right-aligned'
            style={btnWrapperStyle}
        >
            <OnlinkButton
                className='primary add-equipment-button'
                onClick={() => openManualDataEntryDialog({
                    dataGroup: form.dataGroup,
                    dataTypeTitle: label,
                    onClose,
                    translations
                })}
                startIcon={isMobile ? null : <IconAdd/>}
                variant={variant}
            >
                {addButtonLabel}
            </OnlinkButton>
        </div>
    );
}

function ManualDataHistoryTable(props) {
    const {
        addButtonLabel,
        closeConfirmation,
        currencyPreference,
        form,
        isMobile,
        label,
        onCloseEntryDialog,
        openConfirmation,
        openManualDataEntryDialog,
        translations
    } = props;

    const [rows, setRows] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [columns, setColumns] = React.useState(() => []);

    async function fetchManualDataHistory(isMounted) {
        setLoading(true);

        const {manualDataHistory} = await getManualDataHistory(form.dataGroup);

        if (isMounted()) {
            const formattedManualData = formatManualData(manualDataHistory, translations, currencyPreference);

            setRows(formattedManualData);
            setLoading(false);
        }
    }

    async function onClose() {
        await fetchManualDataHistory(() => true);
        onCloseEntryDialog();
    }

    function handleManualDataDelete(data) {
        openConfirmation({
            message: translations.ONLINK_DELETE_ENTRY_WARNING,
            title: translations.ONLINK_DELETE_ENTRY,
            async onContinue() {
                closeConfirmation();
                await deleteManualData(data.manualDataId);
                await onClose();
            },
            onCancel: closeConfirmation
        });
    }

    useDeepEffect(() => fetchEffectData(async (isMounted) => {
        getDataGridColumns(translations, form, openManualDataEntryDialog, onClose, label, setColumns);
        await fetchManualDataHistory(isMounted);
    }), [form]);

    const canDelete = isAuthorized({
        myJdPermissions: DELETE_COURSE_MEASUREMENT_DATA
    }, props);

    const canAdd = isAuthorized({
        myJdPermissions: ADD_COURSE_MEASUREMENT_DATA
    }, props);

    return (
        <div className='settings-container data-grid'>
            <DataGridWrapper
                columns={columns}
                deleteHandler={handleManualDataDelete}
                filterComponent={canAdd && !isMobile ? getAddButton(translations, form, openManualDataEntryDialog, onClose, label, addButtonLabel, isMobile) : null}
                {...(form.dataGroup === WATER_FLOW && {
                    getEstimatedRowHeight: () => ESTIMATED_ROW_HEIGHT,
                    getRowHeight: () => 'auto',
                    sx: {
                        '& .MuiDataGrid-cell': {
                            alignItems: 'flex-start',
                            lineHeight: 'normal',
                            py: 1,
                            whiteSpace: 'normal'
                        }
                    }
                })}
                getRowId={(row) => row.manualDataId}
                initialState={{
                    sorting: {
                        sortModel: [{
                            field: 'timeSample',
                            sort: 'desc'
                        }]
                    }
                }}
                loading={loading}
                rows={rows}
                searchable={true}
                showDeleteColumn={canDelete}
                slotProps={{
                    root: {
                        disableColumnMenu: true
                    }
                }}
                translations={translations}
            />
            {
                canAdd && isMobile && getAddButton(translations, form, openManualDataEntryDialog, onClose, label, addButtonLabel, isMobile)
            }
        </div>
    );
}

ManualDataHistoryTable.propTypes = {
    addButtonLabel: PropTypes.string,
    closeConfirmation: PropTypes.func,
    currencyPreference: PropTypes.string,
    form: PropTypes.object,
    isMigrated: PropTypes.bool,
    isMobile: PropTypes.bool,
    label: PropTypes.string,
    myJdPermissions: PropTypes.myJdPermissions,
    onCloseEntryDialog: PropTypes.func,
    openConfirmation: PropTypes.func,
    openManualDataEntryDialog: PropTypes.func,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        currencyPreference: state.membership.currencyPreference,
        isMigrated: state.membership.isMigrated,
        myJdPermissions: state.account.myJdPermissions
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        openManualDataEntryDialog(props) {
            dispatch(openDialog(dialogTypes.MANUAL_DATA_ENTRY_DIALOG, props));
        },
        openConfirmation(props) {
            dispatch(openDialog(dialogTypes.CONFIRMATION_DIALOG, props));
        },
        closeConfirmation() {
            dispatch(closeDialog(dialogTypes.CONFIRMATION_DIALOG));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(MediaQuery(MOBILE_MEDIA_QUERY)(ManualDataHistoryTable));
